import { Users, Target, Shield } from 'lucide-react';

const About = () => {
  return (
    <div className="pt-24 pb-20">
      <div className="container mx-auto">
        {/* Hero Section */}
        <div className="max-w-4xl mx-auto text-center mb-20">
          <h1 className="text-5xl md:text-6xl font-bold heading-font text-white mb-6">
            About Limitly
          </h1>
          <p className="text-xl text-gray-400 leading-relaxed">
            We're building smarter spend controls for small teams who need the power of enterprise tools without the complexity.
          </p>
        </div>

        {/* Story Section */}
        <div className="max-w-3xl mx-auto mb-20">
          <div className="bg-white/5 rounded-2xl border border-white/10 p-8 md:p-12 backdrop-blur-sm">
            <h2 className="text-3xl font-bold heading-font text-white mb-6">
              Our Story
            </h2>
            <div className="space-y-4 text-gray-300 leading-relaxed">
              <p>
                Limitly started from a simple observation: small businesses and agencies struggle with the same expense management challenges as large enterprises, but existing solutions are either too complex or too limited.
              </p>
              <p>
                We built Limitly to bridge that gap. Our platform gives you powerful controls over team spending, budget limits, merchant restrictions, receipt capture, and audit-ready exports, without requiring a finance degree to set up.
              </p>
              <p>
                Whether you're a freelancer managing contractors, an agency tracking project expenses, or a small business keeping costs under control, Limitly helps you spend smarter and stay organized.
              </p>
            </div>
          </div>
        </div>

        {/* Who It's For Section */}
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold heading-font text-white text-center mb-12">
            Who Limitly is for
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white/5 rounded-2xl border border-white/10 p-8 backdrop-blur-sm hover:border-white/20 transition-all">
              <div className="w-14 h-14 bg-indigo-600/20 rounded-xl flex items-center justify-center mb-6">
                <Users size={28} className="text-indigo-400" />
              </div>
              <h3 className="text-2xl font-semibold heading-font text-white mb-4">
                Freelancers
              </h3>
              <p className="text-gray-400 leading-relaxed">
                Manage business expenses separately from personal spending. Issue cards to contractors with clear limits and track every dollar.
              </p>
            </div>

            <div className="bg-white/5 rounded-2xl border border-white/10 p-8 backdrop-blur-sm hover:border-white/20 transition-all">
              <div className="w-14 h-14 bg-indigo-600/20 rounded-xl flex items-center justify-center mb-6">
                <Target size={28} className="text-indigo-400" />
              </div>
              <h3 className="text-2xl font-semibold heading-font text-white mb-4">
                Agencies
              </h3>
              <p className="text-gray-400 leading-relaxed">
                Track project expenses by team and category. Give team members spending power with built-in controls and automatic receipt capture.
              </p>
            </div>

            <div className="bg-white/5 rounded-2xl border border-white/10 p-8 backdrop-blur-sm hover:border-white/20 transition-all">
              <div className="w-14 h-14 bg-indigo-600/20 rounded-xl flex items-center justify-center mb-6">
                <Shield size={28} className="text-indigo-400" />
              </div>
              <h3 className="text-2xl font-semibold heading-font text-white mb-4">
                Small Businesses
              </h3>
              <p className="text-gray-400 leading-relaxed">
                Control company spending with category limits and merchant controls. Export clean records for accounting and stay audit-ready.
              </p>
            </div>
          </div>
        </div>

        {/* Values Section */}
        <div className="max-w-3xl mx-auto mt-20">
          <h2 className="text-3xl md:text-4xl font-bold heading-font text-white text-center mb-12">
            What we believe
          </h2>
          <div className="space-y-6">
            <div className="bg-white/5 rounded-xl border border-white/10 p-6 backdrop-blur-sm">
              <h3 className="text-xl font-semibold text-white mb-2">Simplicity first</h3>
              <p className="text-gray-400">
                Powerful tools shouldn't require complex setup. We make spend management intuitive from day one.
              </p>
            </div>
            <div className="bg-white/5 rounded-xl border border-white/10 p-6 backdrop-blur-sm">
              <h3 className="text-xl font-semibold text-white mb-2">Control without complexity</h3>
              <p className="text-gray-400">
                Set the rules once, then let the system work. Spend less time managing expenses and more time building your business.
              </p>
            </div>
            <div className="bg-white/5 rounded-xl border border-white/10 p-6 backdrop-blur-sm">
              <h3 className="text-xl font-semibold text-white mb-2">Built for teams</h3>
              <p className="text-gray-400">
                Whether you're a team of 2 or 50, Limitly scales with you. Everyone gets the access they need with the controls you want.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
